/**
 * @file Writes a served period's bounds as a caption in the calendar time
 * zone, such as `"Jan 1 – Aug 6, 2026"`. Consumers: the Analytics page's date
 * caption and the Projects, Orders and Invoicing captions. The served `to` is
 * the exclusive instant the query ran at, so read in the calendar zone it is
 * today's date there, never the browser's. The year is written once when both
 * dates share it, and on both sides when the period starts in an earlier year.
 */
import { DateTime } from 'luxon';

import type { ResolvedAnalyticsPeriod } from '#types';

const LOCALE = 'en-US';

export const analyticsPeriodRangeText = (
  { from, to }: Pick<ResolvedAnalyticsPeriod, 'from' | 'to'>,
  timeZone: string,
): string => {
  const start = DateTime.fromISO(from, { zone: timeZone, locale: LOCALE });
  const end = DateTime.fromISO(to, { zone: timeZone, locale: LOCALE });
  const startText = start.toFormat(
    start.year === end.year ? 'LLL d' : 'LLL d, yyyy',
  );
  return `${startText} – ${end.toFormat('LLL d, yyyy')}`;
};
